import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HelpDocsLinksComponent } from '../help-docs-links/help-docs-links.component';
import { BaseService } from '../services/base.service';
import { NgbDatepickerModule, NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { tap } from 'rxjs/operators';
import { DynamicInputComponent } from '../dynamic-input/dynamic-input.component';

@Component({
  selector: 'app-generic-search-bar',
  templateUrl: './generic-search-bar.component.html',
  styleUrls: ['./generic-search-bar.component.scss'],
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    HelpDocsLinksComponent,
    NgbDropdownModule,
    NgbDatepickerModule,
    DynamicInputComponent,
  ]
})
export class GenericSearchBarComponent implements OnInit {
  @Input() service: BaseService;
  @Input() filterFields: string[] = [];
  @ViewChild('filterMenu') filterMenu: NgbDropdown;

  searchText: string = '';
  filterValues: { [key: string]: any } = {};
  activeFilters: { key: string; display: string }[] = [];
  fieldMeta: { [key: string]: any } = {};
  filterMeta: { fields: { [key: string]: any } } = { fields: {} };
  metaLoaded = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(params => {
      this.searchText = params['search'] || '';
      this.filterFields.forEach(f => {
        if (params[f] !== undefined) {
          this.filterValues[f] = params[f];
        }
      });
      this.rebuildActiveFilters();
    });

    if (this.service && this.filterFields.length > 0) {
      this.service.loadMetadata().pipe(
        tap(meta => {
          if (meta?.fields) {
            this.filterFields.forEach(f => {
              if (meta.fields[f]) {
                this.fieldMeta[f] = meta.fields[f];
                // Create filterMeta for DynamicInputComponent, removing constraints like required
                this.filterMeta.fields[f] = {
                  ...meta.fields[f],
                  required: false,
                  read_only: false,
                  hasError: false
                };
              }
            });
            this.metaLoaded = true;
          }
        })
      ).subscribe();
    }
  }

  get showFilter(): boolean {
    return !!this.service && this.filterFields.length > 0;
  }


  applyFilter(): void {
    const queryParams: any = { search: this.searchText, page: 1 };
    this.filterFields.forEach(f => {
      queryParams[f] = this.filterValues[f] ?? null;
    });
    this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
    this.filterMenu?.close();
  }

  clearFilters(): void {
    this.filterValues = {};
    const queryParams: any = { search: this.searchText, page: 1 };
    this.filterFields.forEach(f => queryParams[f] = null);
    this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
    this.filterMenu?.close();
  }

  clearActiveFilter(key: string): void {
    this.filterValues[key] = null;
    this.router.navigate([], {
      queryParams: { [key]: null },
      queryParamsHandling: 'merge'
    });
  }

  search(): void {
    const queryParams: any = { search: this.searchText };
    const currentParams = this.activatedRoute.snapshot.queryParams;
    if (currentParams['page'] !== undefined) {
      queryParams['page'] = 1;
    }
    this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
  }

  clearSearch(): void {
    this.searchText = '';
    this.router.navigate([], {
      queryParams: { search: '' },
      queryParamsHandling: 'merge'
    });
  }

  private rebuildActiveFilters(): void {
    this.activeFilters = [];
    this.filterFields.forEach(key => {
      const val = this.activatedRoute.snapshot.queryParams[key];
      if (val !== undefined && val !== null && val !== '') {
        const label = this.fieldMeta[key]?.label || key;
        const choices = this.fieldMeta[key]?.choices || this.fieldMeta[key]?.all_choices || [];
        const choice = choices.find(c => c.value == val);
        const display = choice ? `${label}: ${choice.display_name}` : `${label}: ${val}`;
        this.activeFilters.push({ key, display });
      }
    });
  }
}
